import React from "react";
import styled from "@emotion/styled";
import classNames from "classnames";
import { Flex } from "styles/utility-components";

import UIcon from "lib/icons/untitled_ui/UIcon";
import { ProHelperDataTypes } from "lib/providers/ProHelper";
import { sysTextDefault, white } from "lib/themes/colors";
import { DataTypeNameWithBuyersAndSuppliers, SignalCategory } from "lib/types/models";
import {
  AccountBriefingPaywall,
  AccountBriefingPaywallSmall,
  AwardsLockedPaywall,
  BidNoBidReportsPaywall,
  BidResponsesPaywall,
  BidStudioPaywall,
  BidWinThemesPaywall,
  BuyersPaywall,
  BuyersPaywallSmall,
  CMSIntegrationsPaywall,
  CompetitorsPaywall,
  CompetitorsPaywallSmall,
  ContactsPaywall,
  ContactsPaywallSmall,
  ContractExpiriesPaywall,
  ContractExpiriesPaywallSmall,
  DocumentsPaywall,
  DocumentsPaywallSmall,
  ExportsPaywall,
  FrameworksPaywall,
  PartnersPaywall,
  PartnersPaywallSmall,
  SpendPaywall,
  SpendPaywallSmall,
  StotlesAIPaywall,
  SuppliersPaywall,
  SuppliersPaywallSmall,
} from "../../../assets/images/paywall/paywall_svgs_index";

import css from "./paywallUtils.module.scss";

// ProHelperDataTypes is a subset of FeatureType
export type FeatureType =
  // If changing FeatureType, make sure to update the paywallContentTestCases in PaywallTestHelpers.ts to match
  | "AWARDS"
  | "AWARDS_LOCKED"
  | "COMPETITORS"
  | "PARTNERS"
  | "BUYERS"
  | "SUPPLIERS"
  | "CONTACTS"
  | "SPEND"
  | "DOCUMENTS"
  | "ACCOUNT_BRIEFINGS"
  | "FRAMEWORKS"
  | "EXPORTS"
  | "BIDS"
  | "STRATEGY"
  | "CREATE_BIDS"
  | "BID_NO_BID_REPORTS"
  | "BID_RESPONSES"
  | "BID_RESPONSE_EXPORTS"
  | "BID_STOTLES_AI"
  | "BID_WIN_THEMES"
  | "CMS_INTEGRATIONS";

type PaywallType = "BANNER" | "MODAL" | "POPOVER";

/**
 * Even though most of the time, ProHelperDataTypes fits neatly into FeatureType as it is a subset of it,
 * there are some cases where it can be undefined. To ensure a safe conversion, we'll default to SUPPLIERS as
 * that seems like the most likely/common ambigious case we have
 */
export function convertDataTypeToFeatureType(datatype?: ProHelperDataTypes): FeatureType {
  return datatype ? (datatype as FeatureType) : "SUPPLIERS";
}

/**
 * Currently the toggle to enable data sources in the back end doesn't match FeatureType so we have to do this
 * @param featureType
 * @returns
 */
export function convertFeatureTypeToDataType(
  featureType: FeatureType,
): DataTypeNameWithBuyersAndSuppliers {
  // TODO: These features are currently not paywalled via data sources toggle in admin panel, but there are plans to do so
  const unblockedFeatures: FeatureType[] = ["ACCOUNT_BRIEFINGS"];

  const extraFeatures: FeatureType[] = ["SPEND", "COMPETITORS", "PARTNERS", "EXPORTS"];

  // Effectively a no-op
  if (unblockedFeatures.includes(featureType)) {
    return "TENDERS|PRE-TENDERS";
  } else if (extraFeatures.includes(featureType)) {
    return "BUYERS|SUPPLIERS";
  } else return featureType as DataTypeNameWithBuyersAndSuppliers;
}

/**
 * Converts signal category to corresponding feature type. We prioritisie relating the paywall content to the redacted signal category,
 * but if it isn't a competitor signal or a partner signal, we'll fall back to the datatype (which mostly has a corresponding FeatureType)
 * @param signalCategory
 */
export function convertSignalCategoryToFeatureType(
  signalCategory: SignalCategory,
  fallbackdDatatype?: ProHelperDataTypes,
): FeatureType {
  switch (signalCategory) {
    case "Competitors":
      return "COMPETITORS";
    case "Partners":
      return "PARTNERS";
    default:
      // Most of the time, this will default to SUPPLIERS, but if the dataType is actually defined -
      // it will show the paywall relating to that datatype
      return convertDataTypeToFeatureType(fallbackdDatatype);
  }
}

type Content = {
  title: string;
  description: string;
  bullets?: string[];
  paidTagText?: string;
  videoUrl?: string;
};

type PaywallContent = Record<FeatureType, Content>;

export function getPaywallContent(featureType: FeatureType): Content {
  const paywallContent: PaywallContent = {
    AWARDS: {
      title: "Upgrade to unlock expiring contracts",
      description:
        "Identify expiring contracts that can be used to spot upcoming opportunities and build relationships with the buyer, before the tender is written.",
    },

    AWARDS_LOCKED: {
      title: "Upgrade for full access to expiring contracts",
      description:
        "You have reached the free viewing limit of expiring contracts. For full access to continue building proactive pipeline, upgrade your plan.",
    },

    PARTNERS: {
      title: "Upgrade to unlock partner intelligence",
      description:
        "Map your partner network to identify contracts they've won, as well as upcoming opportunities you can pursue.",
    },

    COMPETITORS: {
      title: "Upgrade to unlock competitor intelligence",
      description:
        "Map your competitors to identify contracts they've won and upcoming opportunities you can infiltrate.",
    },

    SUPPLIERS: {
      title: "Upgrade to unlock supplier intelligence",
      description:
        "View profiles of potential partners and competitors, identifying contracts they've won and upcoming opportunities you can pursue.",
    },

    BUYERS: {
      title: "Upgrade to unlock buyer intelligence",
      description:
        " View profiles for target accounts and existing customers, identifying contracts they've won and upcoming opportunities you can pursue. ",
    },

    CONTACTS: {
      title: "Upgrade to unlock decision maker contacts",
      description: "Go beyond procurement contacts and reach out to decision makers directly.",
    },

    DOCUMENTS: {
      title: "Upgrade to unlock strategic documents",
      description:
        "Get access to thousands of buyer-related documents, including budgets, meeting minutes, delivery plans, strategy documents, and more.",
    },

    SPEND: {
      title: "Upgrade to unlock spend data",
      description:
        "Analyse invoice-level spend data that helps you uncover deeper relationships, opportunities, and insights.",
    },

    ACCOUNT_BRIEFINGS: {
      title: "Upgrade to unlock custom briefings, tailored to you",
      description:
        "A suite of custom briefings designed to help commercial teams save hundreds of hours researching and spend more time closing deals.",
    },

    FRAMEWORKS: {
      title: "Upgrade to unlock Framework data",
      description:
        "Sift through thousands of frameworks in minutes to discover and qualify the most valuable opportunities and routes to market for your business.",
    },

    EXPORTS: {
      title: "Export your search results to CSV and XLSX",
      description:
        "Download unlimited rows to perform your own custom analysis powered by Stotles data.",
    },

    BIDS: {
      title: "Upgrade to create unlimited bid / no-bid reports",
      description:
        "You've hit your limit of free bid / no-bid reports. Upgrade to let our team know you're interested in Bid Studio.",
      videoUrl:
        "https://5746318.fs1.hubspotusercontent-na1.net/hubfs/5746318/Tender%20qualification.mp4",
      paidTagText: "BID STUDIO",
    },

    CREATE_BIDS: {
      title: "Unlock Bid Studio to turn your pipeline into bid proposals in minutes.",
      description:
        "Draft bids that pull from the UK's most complete source of government intelligence.",
      bullets: [
        "Qualify your pipeline opportunities in seconds with AI generated bid/no-bid reports",
        "Collaborate with your entire bid team in a single workspace.",
        "Search, organise, and leverage your own content in the saved library.",
        "Auto-generate first draft proposals with our AI co-pilot that speak your customer's language.",
      ],
      paidTagText: "BID STUDIO",
      // videoUrl:
      //   "https://5746318.fs1.hubspotusercontent-na1.net/hubfs/5746318/Tender%20qualification.mp4",
    },

    STRATEGY: {
      title: "Uncover your true TAM - and the best routes to access it.",
      description:
        "Size and sort your market—break it down by territories, accounts, and frameworks to build a sharper go-to-market strategy.",
      videoUrl:
        "https://5746318.fs1.hubspotusercontent-na1.net/hubfs/5746318/Sizing%20your%20market.mp4",
    },

    BID_NO_BID_REPORTS: {
      title: "Upgrade to unlock more bid/no bid reports",
      description:
        "Get access to more tender document summaries that help you understand the bid requirements and guide your bid/no-bid decision",
    },

    BID_RESPONSES: {
      title: "Upgrade to unlock bid responses",
      description:
        "Use AI to extract bid questions from tender document and draft winning bids, as well as manage your bid writing team's progress",
    },

    BID_RESPONSE_EXPORTS: {
      title: "Upgrade to unlock exports",
      description: "You need to be on a paid bid seat to have access to exports.",
    },

    BID_STOTLES_AI: {
      title: "Upgrade to unlock Stotles AI",
      description:
        "Get access to powerful summarisation, ask questions of the tender documents and analyse requirements against your content library.",
    },

    BID_WIN_THEMES: {
      title: "Upgrade to unlock win themes.",
      description:
        "Use Stotles AI to recommend potential win themes connecting the dots across market data, your content library and the requirements. Available as part of Bid Studio plans.",
    },

    CMS_INTEGRATIONS: {
      title: "Upgrade to use content library and integrations",
      description:
        "Stotles content library and integration with your existing content library are available as part of paid Bid Studio plans.",
    },
  };

  return paywallContent[featureType];
}

export function PaidTag({ text }: { text?: string }): JSX.Element {
  return (
    <PaidTagContainer>
      <UIcon size={14} icon="lock01" /> {text || "PAID FEATURE"}
    </PaidTagContainer>
  );
}

const PaidTagContainer = styled(Flex)({
  backgroundColor: sysTextDefault,
  color: white,
  alignSelf: "flex-start",
  alignItems: "center",
  gap: "4px",
  padding: "4px 8px",
  borderRadius: "4px",
});

// NB: Some feature paywalls e.g. Bid & Strategy do not use this structure
export const paywallImg = (featureType: FeatureType, context: PaywallType | undefined): string => {
  switch (featureType) {
    case "AWARDS":
      return context === "POPOVER" ? ContractExpiriesPaywallSmall : ContractExpiriesPaywall;

    case "AWARDS_LOCKED":
      return AwardsLockedPaywall;

    case "COMPETITORS":
      return context === "POPOVER" ? CompetitorsPaywallSmall : CompetitorsPaywall;

    case "PARTNERS":
      return context === "POPOVER" ? PartnersPaywallSmall : PartnersPaywall;

    case "SUPPLIERS":
      return context === "POPOVER" ? SuppliersPaywallSmall : SuppliersPaywall;

    case "BUYERS":
      return context === "POPOVER" ? BuyersPaywallSmall : BuyersPaywall;

    case "CONTACTS":
      return context === "POPOVER" ? ContactsPaywallSmall : ContactsPaywall;

    case "SPEND":
      return context === "POPOVER" ? SpendPaywallSmall : SpendPaywall;

    case "DOCUMENTS":
      return context === "POPOVER" ? DocumentsPaywallSmall : DocumentsPaywall;

    case "ACCOUNT_BRIEFINGS":
      return context === "POPOVER" ? AccountBriefingPaywallSmall : AccountBriefingPaywall;

    case "FRAMEWORKS":
      return FrameworksPaywall;

    case "EXPORTS":
      return ExportsPaywall;

    case "CREATE_BIDS":
      return BidStudioPaywall;

    case "BID_NO_BID_REPORTS":
      return BidNoBidReportsPaywall;

    case "BID_RESPONSES":
      return BidResponsesPaywall;

    case "BID_STOTLES_AI":
      return StotlesAIPaywall;

    case "BID_WIN_THEMES":
      return BidWinThemesPaywall;

    case "CMS_INTEGRATIONS":
      return CMSIntegrationsPaywall;

    default:
      throw new Error(`Paywall image not found for feature type: ${featureType}`);
  }
};

// Larger images that go on banners & modals, smaller ones go on popovers. The difference
// between the two types of images is that the "Small" ones have less detail within them
type PaywallImgProps = {
  context: PaywallType;
  featureType: FeatureType;
  className?: string;
};

export function PaywallImg({ context, featureType, className }: PaywallImgProps): JSX.Element {
  const paywallBackground = (featureType: FeatureType): string => {
    switch (featureType) {
      case "COMPETITORS":
      case "SPEND":
        return css.redStotlesBackground;

      case "DOCUMENTS":
      case "PARTNERS":
        return css.yellowStotlesBackground;

      case "SUPPLIERS":
      case "BUYERS":
        return css.purpleStotlesBackground;

      case "BID_NO_BID_REPORTS":
      case "BID_RESPONSES":
      case "BID_STOTLES_AI":
      case "BID_WIN_THEMES":
      case "CMS_INTEGRATIONS":
        return css.greyStotlesBackground;

      default:
        return css.blueStotlesBackground;
    }
  };

  const paywallImage = paywallImg(featureType, context);

  return (
    <div
      data-testid="paywall-img-container"
      className={classNames(
        paywallBackground(featureType),
        {
          [css.bannerImg]: context === "BANNER",
          [css.modalImg]: context === "MODAL",
        },
        className,
      )}
    >
      <img
        src={paywallImage}
        alt="Paywall"
        className={context === "BANNER" ? css.imgLarge : css.img}
      />
    </div>
  );
}

export function PaywallVideo({ featureType }: { featureType: FeatureType }) {
  const { videoUrl } = getPaywallContent(featureType);

  return (
    <div className={css.videoContainer}>
      <video controls controlsList="nodownload" className={css.video}>
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
}
